import { Icons, HexIcon, Typography, Stack } from '@cast/design-system';

import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';

import { ChartMoney } from './_components/ChartMoney';
import { ChartTitle } from './_components/ChartTitle';
import { makeChartTooltip } from './_components/ChartTooltip';
import { CostPerProvisionedResourceChart } from './_components/CostPerProvisionedResourceChart';
import { useNormalizedCostsControls } from './_hooks/useNormalizedCostControls';
import {
  DataPointMapper,
  useNormalizedResourceCostChartData,
} from './_hooks/useNormalizedResourceCostChartData';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const chartDataMapper: DataPointMapper = ({ costPerStorage }) => ({
  normalizedCost: costPerStorage,
  onDemandCost: null,
  spotCost: null,
  fallbackCost: null,
});

type Props = { costType: string };

export const StorageCostChart = ({ costType }: Props) => {
  const { cluster } = useCluster();
  const { getRange } = useCostReportTimeRangeSelection();
  const { isLoading, data } =
    useNormalizedResourceCostChartData(chartDataMapper);
  const { activeTab } = useNormalizedCostsControls();
  const { tooltipHeader, tooltipFooter } = makeChartTooltip({
    unit: 'STORAGE',
  });
  const isNotSupported = activeTab !== 'normalized';

  return (
    <CostPerProvisionedResourceChart
      header={
        <ChartTitle
          title={`${costType} STORAGE COST`}
          infoTooltip="Total daily storage spend divided by the provisioned storage"
          cost={
            <ChartMoney
              mapper={({ hourlyCostPerStorage }) => hourlyCostPerStorage}
            />
          }
        />
      }
      tooltipHeader={isNotSupported ? undefined : tooltipHeader}
      tooltipFooter={isNotSupported ? undefined : tooltipFooter}
      data={data}
      loading={isLoading}
      chartOverlay={
        isNotSupported && (
          <Stack alignItems="center" pt={22} gap={4}>
            <HexIcon
              variant="notification"
              size={72}
              icon={<Icons.Question />}
            />
            <Typography variant="P12R" color="textSecondary">
              This resource offering is not supported for storage.
            </Typography>
          </Stack>
        )
      }
      testId="cost-per-provisioned-resource-chart-storage"
      buildPrompt={buildCostReportAnalyzePrompt({
        scopeName: cluster?.name,
        context: 'storage cost per provisioned resource',
        getDefaultRange: getRange,
      })}
    />
  );
};
